/**
 * Data layer for the COMPANY panel's Cessation section — Last Puff (quitting
 * smoking and vaping) and Last Drink (quitting alcohol).
 *
 * NOBODY IS NAMED ANYWHERE IN THIS SECTION, and there is no fetcher here that
 * could name them. Every other company section has a users list and a
 * per-person page; this one deliberately has neither. Somebody taking a skin
 * scan tells you nothing about them — somebody enrolled in Last Drink has told
 * you they have a drinking problem. Enrolment is the disclosure, it is
 * special-category health data, and an employer must not be handed the list.
 *
 * SMALL COHORTS COME BACK EMPTY. Under the backend's minimum the payload is
 * `{ suppressed: true, minimum }` and carries no figures at all — not even the
 * participant count. The department filter can narrow a company to a handful of
 * people, and at that size an average is one person's relapse. Pages must check
 * `suppressed` before reading anything else.
 *
 * FREE TEXT NEVER ARRIVES. Motivations are matched against the server's own
 * catalogue inside the pipeline; triggers and quit reasons, which have no
 * catalogue, are only returned when enough distinct people chose the same
 * label. `custom_motivations` and `fears_concerns` are never read at all. See
 * Backend services/cessationHubService.js for the full reasoning.
 *
 * Backed by the company/v1 cessation-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Both
 * fetchers take an optional `department` so the section's filter narrows the
 * whole page.
 */

import {
  getCompanyCessationDashboard,
  getCompanyCessationProgramme
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Both programmes side by side, plus how many people are on both. */
export const adaptOverview = (data) => {
  if (data?.suppressed) {
    return { suppressed: true, minimum: data.minimum ?? 0, programmes: data.programmes || [] };
  }

  return {
    suppressed: false,
    minimum: data.minimum ?? 0,
    totalUsers: data.totalUsers ?? 0,

    // Distinct people on either programme — somebody on both counts once.
    participants: data.participants ?? 0,
    adoption: data.adoption ?? 0,
    bothProgrammes: data.bothProgrammes ?? 0,
    overlapSplit: data.overlapSplit || [],

    activeUsers: data.activeUsers ?? 0,
    logs: data.logs ?? 0,
    slipFreeWeek: data.slipFreeWeek ?? 0,

    // A row per programme. Each may carry `suppressed: true` on its own, so the
    // overview can report a combined total while withholding a programme that
    // is too small to report by itself.
    programmes: data.programmes || []
  };
};

/** One programme's page. */
export const adaptProgramme = (data) => {
  if (data?.suppressed) {
    return {
      suppressed: true,
      key: data.key || null,
      label: data.label || '',
      minimum: data.minimum ?? 0
    };
  }

  return {
    ...data,
    suppressed: false,
    label: data.label || '',
    subject: data.subject || '',
    slipWord: data.slipWord || 'slipped',

    // Reach
    totalUsers: data.totalUsers ?? 0,
    participants: data.participants ?? 0,
    adoption: data.adoption ?? 0,
    activeUsers: data.activeUsers ?? 0,
    neverLogged: data.neverLogged ?? 0,

    // How it is going
    slipFreeWeek: data.slipFreeWeek ?? 0,
    slipFreeMonth: data.slipFreeMonth ?? 0,
    neverSlipped: data.neverSlipped ?? 0,
    avgSlipFreeDays: data.avgSlipFreeDays ?? 0,
    avgDaysTracked: data.avgDaysTracked ?? 0,
    bestRun: data.bestRun ?? 0,
    slipFreeDayRate: data.slipFreeDayRate ?? 0,
    loggedDays: data.loggedDays ?? 0,

    // Logging
    logs: data.logs ?? 0,
    checkins: data.checkins ?? 0,
    cravings: data.cravings ?? 0,
    cravingsResisted: data.cravingsResisted ?? 0,
    cravingResistRate: data.cravingResistRate ?? 0,
    avgIntensity: data.avgIntensity ?? null,
    dailyReturnRate: data.dailyReturnRate ?? 0,
    weeklyReturnRate: data.weeklyReturnRate ?? 0,

    // Distributions
    runSplit: data.runSplit || [],
    bestRunSplit: data.bestRunSplit || [],
    tenureSplit: data.tenureSplit || [],
    intensitySplit: data.intensitySplit || [],
    activeDaySplit: data.activeDaySplit || [],
    durationSplit: data.durationSplit || { title: '', rows: [] },
    attemptSplit: data.attemptSplit || [],

    // Cohort-gated lists — may legitimately be empty when no single label
    // cleared the threshold, which is not the same as nobody choosing one.
    topMotivations: data.topMotivations || [],
    topReasons: data.topReasons || [],
    topTriggers: data.topTriggers || [],

    // Series. Backend zero-fills the last 30 days; charts consume plain arrays.
    weekdayCravings: data.weekdayCravings || [],
    logsTrend: (data.trend || []).map((day) => day.logs || 0),
    peopleTrend: (data.trend || []).map((day) => day.people || 0),

    // { currencies: [{currency, amount}], totals: [{key,label,unit,value,people}] }
    // Money is per currency and never summed across them — these profiles each
    // carry their own, and adding GBP to INR produces a number that is not money.
    impact: data.impact || { currencies: [], totals: [] }
  };
};

export const fetchCompanyCessationOverview = async (department = '') => {
  try {
    return adaptOverview(unwrap(await getCompanyCessationDashboard(department)));
  } catch (error) {
    console.error('fetchCompanyCessationOverview error:', error);
    return null;
  }
};

export const fetchCompanyCessationProgramme = async (programme, department = '') => {
  try {
    return adaptProgramme(unwrap(await getCompanyCessationProgramme(programme, department)));
  } catch (error) {
    console.error('fetchCompanyCessationProgramme error:', error);
    return null;
  }
};
